import React, { useState, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SlidersHorizontal, Search, Grid3x3, Map } from "lucide-react";
import PropertyCard from "./PropertyCard";
import { ListingsMap } from "./PropertyMap";
import { fetchProperties } from "../api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const PropertyListing = ({ status, title, subtitle, heroImage }) => {
  const locationHook = useLocation();
  const [query, setQuery] = useState("");
  const [beds, setBeds] = useState("any");
  const [sort, setSort] = useState("newest");
  const [type, setType] = useState("any");
  const [city, setCity] = useState("all");
  const [properties, setProperties] = useState([]);
  const [allTypes, setAllTypes] = useState(["any"]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("grid");

  useEffect(() => {
    const params = new URLSearchParams(locationHook.search);
    const q = params.get("q");
    if (q) setQuery(q);
  }, [locationHook.search]);

  // Load full set once to derive available property types
  useEffect(() => {
    fetchProperties({ status })
      .then((data) => {
        const set = new Set(data.map((p) => p.type));
        setAllTypes(["any", ...Array.from(set)]);
      })
      .catch(() => {});
  }, [status]);

  // Fetch filtered/sorted results whenever controls change (debounced for search)
  useEffect(() => {
    setLoading(true);
    const handle = setTimeout(() => {
      fetchProperties({
        status,
        q: query,
        min_beds: beds !== "any" ? parseInt(beds) : undefined,
        type,
        city: city !== "all" ? city : undefined,
        sort,
      })
        .then((data) => setProperties(data))
        .catch(() => setProperties([]))
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [status, query, beds, type, city, sort]);

  const types = useMemo(() => allTypes, [allTypes]);
  const filtered = properties;

  return (
    <div>
      <section className="relative h-[320px] flex items-center justify-center">
        <div className="absolute inset-0">
          <img src={heroImage} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 text-center px-5">
          <p className="text-[12px] tracking-[0.28em] uppercase text-[#e8d4b8] mb-3">{subtitle}</p>
          <h1 className="font-display text-white text-[40px] md:text-[52px] font-300">{title}</h1>
        </div>
      </section>

      <section className="bg-white border-b border-[#f0ece5] sticky top-[74px] z-30">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 py-5">
          <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
            <div className="inline-flex rounded-full bg-[#faf7f2] border border-[#e2d8c8] p-1 self-start lg:self-auto" data-testid="city-filter">
              {[["all", "All areas"], ["bath", "Bath"], ["bristol", "Bristol"]].map(([v, label]) => (
                <button
                  key={v}
                  onClick={() => setCity(v)}
                  data-testid={`city-filter-${v}`}
                  className={`px-4 py-2.5 rounded-full text-[13px] font-500 transition-colors ${city === v ? "bg-[#b8935f] text-white" : "text-[#6a6a6a] hover:text-[#2b2b2b]"}`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="relative flex-1">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#b8935f]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search area or postcode"
                className="w-full h-[50px] pl-11 pr-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f] transition"
              />
            </div>
            <div className="flex flex-wrap gap-3">
              <Select value={type} onValueChange={setType}>
                <SelectTrigger className="w-[150px] h-[50px] border-[#e2d8c8]">
                  <SelectValue placeholder="Property type" />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t} value={t}>{t === "any" ? "Any type" : t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={beds} onValueChange={setBeds}>
                <SelectTrigger className="w-[140px] h-[50px] border-[#e2d8c8]">
                  <SelectValue placeholder="Bedrooms" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any beds</SelectItem>
                  <SelectItem value="1">1+ beds</SelectItem>
                  <SelectItem value="2">2+ beds</SelectItem>
                  <SelectItem value="3">3+ beds</SelectItem>
                  <SelectItem value="4">4+ beds</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="w-[160px] h-[50px] border-[#e2d8c8]">
                  <SlidersHorizontal size={15} className="text-[#b8935f]" />
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest first</SelectItem>
                  <SelectItem value="price-asc">Price: Low to High</SelectItem>
                  <SelectItem value="price-desc">Price: High to Low</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#faf7f2] min-h-[50vh]">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <p className="text-[14px] text-[#6a6a6a]">
              Showing <span className="text-[#2b2b2b] font-500">{filtered.length}</span>{" "}
              {status === "for-sale" ? "properties for sale" : "properties to rent"}
            </p>
            <div className="flex gap-2 bg-white p-1.5 rounded-full border border-[#f0ece5]">
              {[["grid", "Grid", Grid3x3], ["map", "Map", Map]].map(([v, label, Icon]) => (
                <button key={v} onClick={() => setView(v)} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] transition-colors ${view === v ? "bg-[#b8935f] text-white" : "text-[#6a6a6a] hover:text-[#2b2b2b]"}`}>
                  <Icon size={15} /> {label}
                </button>
              ))}
            </div>
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-display text-[22px] text-[#2b2b2b] mb-2">
                {loading ? "Loading properties..." : "No properties found"}
              </p>
              {!loading && <p className="text-[14px] text-[#8a8a8a]">Try adjusting your search or filters.</p>}
            </div>
          ) : view === "map" ? (
            <div className="grid lg:grid-cols-[1fr_1.1fr] gap-7">
              <div className="grid sm:grid-cols-2 gap-6 content-start">
                {filtered.map((p, i) => (
                  <PropertyCard key={`${p.id}-${i}`} property={p} />
                ))}
              </div>
              <ListingsMap properties={filtered} />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {filtered.map((p, i) => (
                <PropertyCard key={`${p.id}-${i}`} property={p} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default PropertyListing;
